export interface TransactionIdResponse {
  transactionId: string | null;
}

export interface EntityRef {
  entityId: string;
  entityType: string;
}

export interface LoanDetail {
  applications: LoanApplication[];
  lenderCaseIdentifier: string;
  organizationCode: string;
  id: string;
  loanNumber: string;
}

export interface LoanApplication {
  borrower: Person;
  coborrower?: Person;
  id: string;
}

export interface Person {
  fullNameWithSuffix: string;
  firstName: string;
  lastName: string;
  emailAddressText: string;
  taxIdentificationIdentifier: string;
  id: string;
  homePhoneNumber?: string;
}

export interface Product {
  id: string;
  partnerId: string;
  name: string;
}

export interface Credentials {
  clientId: string;
  token: string;
}

export interface OriginatingParty {
  name: string;
  address: string;
  city: string;
  state: string;
  postalCode: string;
  pointOfContact: PointOfContact;
}

export interface PointOfContact {
  name: string;
  email: string;
  phone: string;
  personas: string[];
}

export interface Identity {
  hash: string;
}

export interface Transaction {
  id: string;
  status: string;
  clientId: string;
  environment: string;
  entityRef: EntityRef;
  product: Product;
  request: Request;
  response?: Response;
  application: string;
  orderType: string;
  created: string;
  updated: string;
  createdBy: string;
  events: Event[];
}

export interface Request {
  loanFormat: string;
  loan?: Record<string, unknown>;
  resources?: Resource[];
  credentials: Credentials;
  requestingParty: OriginatingParty;
  options?: Record<string, unknown>;
  type: string;
}

export interface Response {
  // Define response properties as needed
  status?: string | null;
  partnerStatus?: string | null;
  resources?: Resource[];
}

export interface Event {
  // Define event properties as needed
  id: string;
  text?: string;
  type?: string;
  from?: string;
  sent?: string;
  comments?: string;
  resources?: Resource[];
  details?: Record<string, unknown>;
}

export interface Resource {
  id: string;
  name: string;
  mimeType: string;
  location: string;
  authorization: string;
  docType: string;
  source?: string;
  type?: string;
}

export interface Details {
  transactionId: string;
  requestCorrelationId: string;
}

export interface DownloadedFile {
  id: string;
  name: string;
  mimeType: string;
  data: string; // base64
}

export interface TransactionResult<T> {
  statusCode: number;
  response: T;
}

export interface TokenResponse {
  access_token: string;
  expires_in: number;
}
export interface OriginResponse {
  clientId: string;
  originData: OriginData;
  encompassLoanId: string | null;
  transactionId?: string;
  auditDetail?: AuditDetail | null;
}

export interface OriginData {
  id: string;
  entityRef: EntityRef;
  loanFormat: string;
  loan: LoanDetail;
  interfaceUrl: string;
  product: Product;
  credentials?: Credentials; // Optional - excluded from API responses for security
  environment: string;
  originatingParty: OriginatingParty;
  identity: Identity;

  // TODO (future) expand orgin and loan data interfaces
}

export const TransactionTypeEnum = {
  AUS: "Run LoanLight AUS",
} as const;

export type TransactionType =
  (typeof TransactionTypeEnum)[keyof typeof TransactionTypeEnum];

export interface TransactionResponse {
  id: string;
}

export interface transactionRequest {
  resources: Resource[];
  [key: string]: unknown;
}
export interface AuditDetail {
  id: string;
  transactionRecordId: string;
  transactionStateId: string;
  auditState: AuditState;
  updated: string;
  initialRun: string;
  epcTransactionId: string;
  errorMessage?: string | null;
  errorCode?: string | null;
  errorStep?: string | null;
  errorTimestamp?: string | null;
  progressStep?: string | null;
  progressMessage?: string | null;
  progressPercent?: number | null;
  progressUpdatedAt?: string | null;
}

export enum AuditState {
  Processing = "Processing",
  Complete = "Complete",
  Failed = "Failed",
  Error = "Error",
}
